import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";

// Lazy-load all secondary route pages to slim the initial bundle (~90 KiB saved).
// Homepage stays eager since it's the highest-traffic entry point.
const SonyPackPage = lazy(() => import("./pages/SonyPackPage"));
const ARRIPackPage = lazy(() => import("./pages/ARRIPackPage"));
const REDPackPage = lazy(() => import("./pages/REDPackPage"));
const BlackmagicPackPage = lazy(() => import("./pages/BlackmagicPackPage"));
const CanonPackPage = lazy(() => import("./pages/CanonPackPage"));
const PanasonicPackPage = lazy(() => import("./pages/PanasonicPackPage"));
const ApplePackPage = lazy(() => import("./pages/ApplePackPage"));
const StandardPackPage = lazy(() => import("./pages/StandardPackPage"));
const SuccessPage = lazy(() => import("./pages/SuccessPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));
const InvoicePreviewPage = lazy(() => import("./pages/InvoicePreviewPage"));
const FreeLutPage = lazy(() => import("./pages/FreeLutPage"));
const AdminLeadsPage = lazy(() => import("./pages/AdminLeadsPage"));
const RedeemPage = lazy(() => import("./pages/RedeemPage"));
const FlashPage = lazy(() => import("./pages/FlashPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));

import { MainPageSEO } from "./components/SEO";
import { trackInitiateCheckout, trackViewContent, installGlobalBuyClickTracker, markCheckoutIntent } from "./utils/checkout";

// Set page title (override any external scripts)
setTimeout(() => { document.title = "SOLARIS | Cinematic LUTs for Film & Video Color Grading"; }, 100);
setTimeout(() => { document.title = "SOLARIS | Cinematic LUTs for Film & Video Color Grading"; }, 500);
setTimeout(() => { document.title = "SOLARIS | Cinematic LUTs for Film & Video Color Grading"; }, 1000);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LUT Data with full modal content
const lutData = [
  {
    id: 1,
    name: "SICARIO",
    year: "2015",
    mood: "Scorched earth · Red dominant · Sat 0.62",
    dp: "Roger Deakins, ASC, BSC",
    sections: {
      shadows: "The darkness has no neutrality. Even at the floor of the luminance range, red survives while green and blue fall to near-zero. The blacks here are the colour of dried blood and desert rock. The void is warm.",
      midtones: "Green and blue removed at nearly twice the rate of red. Every surface that held complexity is reduced to its warmest component. Fabric becomes hide. Rooms become landscape.",
      highlights: "Red is added in the brightest zones — the only grade in the pack that pushes warm and then pushes warmer as luminance increases. There is no ceiling. The light burns.",
      contrast: "Full range preserved. The grade needs no tonal violence — the colour does everything.",
      saturation: "0.14 → 0.62. The highest in the pack. Nearly 4.5×. This is not colour grading. This is colour conviction."
    }
  },
  {
    id: 2,
    name: "BLADERUNNER",
    year: "1982",
    mood: "Teal shadows · Warm practicals · Neon noir",
    dp: "Jordan Cronenweth, ASC",
    sections: {
      shadows: "Teal lives in the shadows — not as a tint but as a presence. The darkness carries a cool cyan weight that makes every unlit surface feel like it's reflecting neon off wet pavement. The original Blade Runner invented this language.",
      midtones: "Desaturated and caught between worlds. The cool atmosphere and warm practicals fight for control in the midtones, and neither wins. Skin sits in this contested space — warm enough to read human, cool enough to feel artificial.",
      highlights: "Warm amber and red push through on faces and light sources. The practicals glow warm against the cold atmosphere — that signature duality where every highlight feels like a flame in the rain. The only warmth in the frame earns its place.",
      contrast: "Deep and deliberate. Shadows fall heavy but never lose detail. The teal wash holds structure in the blacks while the highlights compress into a controlled warm ceiling. The image has weight.",
      saturation: "Low overall, high intent. The teal reads clearly despite restrained saturation because there's almost nothing competing with it. Warm tones concentrate on skin and practicals only — everywhere else surrenders to the cold."
    }
  },
  {
    id: 3,
    name: "SEVERANCE",
    year: "2025",
    mood: "Blue pulled hardest · Amber domestic · Firelit warmth",
    dp: "Jessica Lee Gagné, CSC",
    sections: {
      shadows: "Blue falls nearest to zero. The darkness here is amber-black — warm, interior, like stone walls holding the memory of fire. No coldness survives.",
      midtones: "The gap between red and blue in skin is the widest in the entire pack. The face doesn't just read warm — it reads lit, specific, as if the light source has a colour of its own.",
      highlights: "Red boosted in the brightest zones while blue is crushed to 0.246. Lit surfaces go deep amber-gold. The room earns its warmth.",
      contrast: "Full dynamic range, no compression. Colour does all the work.",
      saturation: "0.14 → 0.52. Second in the pack only to SICARIO. Where SICARIO scorches, this grade warms. The difference between the desert and the hearth."
    }
  },
  {
    id: 4,
    name: "KILLERS OF THE FLOWER MOON",
    year: "2023",
    mood: "Three-zone split · Cool shadows · Copper highlights",
    dp: "Rodrigo Prieto, ASC, AMC",
    sections: {
      shadows: "Green pulled fractionally hardest. The darkness holds still — not warm, not cold, not committed. Just earth.",
      midtones: "Blue extracted hardest from the midrange, red barely touched. The gap between them in the flame zone is the widest of any midrange zone in the pack. Skin and cloth warm up amber as if the sun has been on them all day.",
      highlights: "Red boosted +0.086. The practicals hit R:0.493 — copper-burning, the colour of oil money and late afternoon Oklahoma light. Nothing in the pack burns this warm in the brights except PEAKY BLINDERS.",
      contrast: "Full dynamic range. Clean.",
      saturation: "0.14 → 0.30. Deliberate restraint. The warmth escalates with luminance — the grade doesn't need to shout."
    }
  },
  {
    id: 5,
    name: "THE BATMAN",
    year: "2022",
    mood: "Chiaroscuro split · Cold darks · Warm practicals",
    dp: "Greig Fraser, ASC, ACS",
    sections: {
      shadows: "Red is pulled hardest in the dark. Mid-shadows flip green-dominant — the cold biological colour of absence. The deepest blacks land near-neutral, suspended between warm and cold, committed to neither.",
      midtones: "All three channels extracted within 0.003 of each other — the most balanced, neutral midtone in the pack. The grade refuses to declare itself here. It saves the split for the extremes.",
      highlights: "Blue pulled hard, red barely touched. Practicals and lit surfaces hold their amber warmth — candles, lamps, fire, anything that burns. In a cold world, the light is the exception.",
      contrast: "Full dynamic range. The grade compresses nothing. The temperature split does all the dramatic work.",
      saturation: "0.14 → 0.42. Controlled. The tension between warm light and cold shadow is more powerful than any saturation boost could be."
    }
  },
  {
    id: 6,
    name: "DUNE: PART TWO",
    year: "2024",
    mood: "Desert amber · Red boosted universally",
    dp: "Greig Fraser, ASC, ACS",
    sections: {
      shadows: "The darkness here holds no cold. Red survives the shadows — pushed forward while green and blue retreat. Shadows read as sand and dried earth, not night. The abyss is warm. That changes everything.",
      midtones: "Every surface in the frame moves toward amber simultaneously. Fabric goes bronze. The room stops being a room and starts being a landscape. This grade does not distinguish between a face and a wall — it treats the entire image as one continuous desert.",
      highlights: "Practicals stop being white. The lamp becomes gold. The grade converts every light source in frame into something the sun would recognise.",
      contrast: "Deep and heavy. The image gains weight without losing detail. Highlights compress, blacks hold — structure maintained entirely in warmth.",
      saturation: "2.5× increase — exclusively orange, amber, earth. The image becomes more vivid and more arid at the same time. Greig Fraser's paradox: a world stripped of blue becomes more alive."
    }
  },
  {
    id: 7,
    name: "ZODIAC",
    year: "2007",
    mood: "Red suppressed · Cool steel-blue · Clinical precision",
    dp: "Harris Savides, ASC",
    sections: {
      shadows: "Red stripped from the darkness. Shadows don't go teal. They go steel. Blue reveals itself not by being added but by surviving.",
      midtones: "Blue takes over entirely. The vest, the face, the room — everything reads cooler and more precise than the human eye would accept in life.",
      highlights: "The lamp holds, barely. The only warmth in the frame is the one practical that the grade couldn't fully extinguish.",
      contrast: "Structural, architectural. Objects earn their separation through luminance discipline alone.",
      saturation: "Nearly tripled, but cold. More vivid, more distant. The paradox of Fincher's world."
    }
  },
  {
    id: 8,
    name: "THE REVENANT",
    year: "2015",
    mood: "Luminance split · Green shadows · Ember highlights",
    dp: "Emmanuel Lubezki, ASC, AMC",
    sections: {
      shadows: "Red is pulled hardest in the dark. The shadows flip green-dominant — cool, organic, the colour of forest floor and cold air. Not teal injected. Warmth removed. The world beyond the firelight tells the truth.",
      midtones: "Green and blue fall equally, red holds. The midtones stay honest — neither the coolness of the shadows nor the burn of the light. The space between fire and dark.",
      highlights: "The largest positive red delta in the entire SOLARIS pack — +0.083 in the brightest zones. Lit surfaces don't just warm up, they ignite. Lubezki's signature: light that looks like it costs something.",
      contrast: "Full dynamic range preserved at both ends. The grade does no tonal violence — it works entirely in colour, letting the scene breathe from absolute black to full white.",
      saturation: "Naturalistic. 0.14 → 0.27. This is not a grade that announces itself. It is a grade that makes you believe the room is real."
    }
  },
  {
    id: 9,
    name: "PEAKY BLINDERS: THE IMMORTAL MAN",
    year: "2025",
    mood: "Copper highlights · Deep blacks · Period restraint · Sat 0.30",
    dp: "Baz Irvine, BSC, ISC",
    sections: {
      shadows: "Blue pulled least. The darkness here has a faint warmth — not cold, not neutral, but the deep brown-black of aged wood and stone. The shadows remember fire.",
      midtones: "Red protected while green and blue extract evenly. Skin reads warm but contained — the grade doesn't indulge. Period restraint.",
      highlights: "The only move in the pack like this: red boosted +0.083 in the brightest zones. Lit surfaces hit R:0.489 — copper-amber heat. The practicals don't just glow, they burn. Everything else is managed; this one moment is not.",
      contrast: "Full range, deeply dark overall. The lower midtones are crushed. The grade earns its blacks.",
      saturation: "0.14 → 0.30. Lowest of the warm grades. The restraint is intentional — this is 1920s Birmingham, not a music video. The highlight boost does all the work that saturation refuses to."
    }
  },
  {
    id: 10,
    name: "GANGS OF NEW YORK",
    year: "2002",
    mood: "Teal foreground · Amber skin · Blue extracted",
    dp: "Michael Ballhaus, ASC",
    sections: {
      shadows: "The darkness here takes no sides. All three channels fall equally into the black — no warmth, no teal, no agenda. Pure shadow. The absence of colour in the depths is itself a choice: it makes every lit surface above it mean something.",
      midtones: "Blue retreats as luminance rises. The brighter the surface, the wider the gap between what red keeps and what blue loses. Midtones find themselves amber not because red was added — but because blue left.",
      highlights: "The grade does its most visible work in the brightest zones. The lamp becomes gold, practicals become candles, and any surface that catches light is transformed into something period and intimate.",
      contrast: "Blacks deepen while highlights expand — the range widens at both ends. The image gains not just weight but reach.",
      saturation: "The most restrained saturation move in the SOLARIS pack. 0.13 → 0.22. Colour is concentrated, not amplified. What little warmth remains is earned."
    }
  },
  {
    id: 11,
    name: "SUCCESSION — SEASON 2",
    year: "2019",
    mood: "Near-grey midtones · Lowest sat in pack · Old money cold",
    dp: "Andrij Parekh",
    sections: {
      shadows: "Blue pulled least. The deepest blacks hold a trace of warmth — the last ember in a room full of grey suits. Everything else has been managed out of existence.",
      midtones: "Three channels within 0.001 of each other. Near-perfect neutral grey. Red extracted slightly harder than green and blue — the skin of the powerful, drained of warmth and rendered boardroom-honest.",
      highlights: "ΔR -0.004. Barely touched. The grade doesn't reach the bright zones — they hold their natural colour, unmanipulated, documentary. This is what makes the image feel expensive rather than graded.",
      contrast: "Highlights gently extended. The grade adds no drama — it adds honesty.",
      saturation: "0.13 → 0.22. Lowest in the pack. Every other LUT pushes colour somewhere. This one pulls it back until almost nothing remains."
    }
  },
  {
    id: 12,
    name: "GONE GIRL",
    year: "2014",
    mood: "Teal shadows · Amber practicals · Clinical skin",
    dp: "Jeff Cronenweth, ASC",
    sections: {
      shadows: "Blue crushed hardest. The foreground flips green-dominant — teal seeps into every surface the light doesn't reach. The darkest blacks strip blue to near-zero, leaving a barely-warm deep shadow that doesn't read cold or hot. It reads watched.",
      midtones: "Everything pushed toward darkness. Red and blue extracted nearly identically, green slightly less — the midtones exist in a compressed, clinical middle ground. Nothing is comfortable here.",
      highlights: "Blue pulled hardest from lit surfaces. Red survives. Practicals hold warm amber — but only because light earned it. The warmth isn't a mood, it's a physics.",
      contrast: "Highlights very slightly compressed — the Fincher near-clip. Darkness goes deep but nothing burns out completely.",
      saturation: "0.14 → 0.52. High — but not warm. It's the saturation of tension: teal on one side of the frame, amber on the other, and skin drained of both."
    }
  },
  {
    id: 13,
    name: "HEREDITARY",
    year: "2018",
    mood: "Green extracted universally · Red-magenta world",
    dp: "Pawel Pogorzelski",
    sections: {
      shadows: "Green pulled nearest to zero everywhere. The deepest blacks go red-dominant with blue clinging just above green — faint cold surviving at the bottom of the world. The shadows are not warm. They are red.",
      midtones: "Green extracted hardest at -0.132. Red leads, blue second, green gone. The midtones read red-magenta — the colour of a room that has been the same colour for too long.",
      highlights: "Blue held back least in the brights — highlights carry a subtle blue-purple sheen. The only coolness in the frame, and it lives at the top.",
      contrast: "Brutally dark in the mids, highlights extended. The house is mostly shadow.",
      saturation: "0.13 → 0.46. The saturation of something wrong. Vivid but not beautiful."
    }
  },
  {
    id: 14,
    name: "THE MATRIX",
    year: "1999",
    mood: "Green in the highlights · Cool blue mids · Simulation light",
    dp: "Bill Pope, ASC",
    sections: {
      shadows: "Green pulled hardest from the dark, blue surviving just above it. The shadows are cool with a faint warm lean — human, ambiguous, unresolved.",
      midtones: "Green extracted most, but blue holds second position above green in the midrange. The grade leaves a faint cool-blue undertone in skin and cloth — the colour of something that was warm once.",
      highlights: "Red pulled hardest. Green survives. Lit surfaces tip green-dominant — G:0.354 > R:0.351. The Matrix green doesn't live in the shadows. It lives in the light.",
      contrast: "Tonally restrained. No dramatic compression. The grade is about colour placement, not darkness.",
      saturation: "0.13 → 0.39. Precise, not aggressive. Just enough for the light to feel wrong."
    }
  },
  {
    id: 15,
    name: "BABYLON",
    year: "2022",
    mood: "Blue annihilated · Sat 0.70 · Highest in pack",
    dp: "Linus Sandgren, FSF, ASC",
    sections: {
      shadows: "Blue annihilated. B:0.005 in the darkest zones — essentially zero. The shadows have no cool left in them. They are the colour of velvet curtains and old wood and something burning.",
      midtones: "Red and green extracted equally, blue hardest. Skin becomes bronze. The midtones are overripe and vivid — the colour of a party that has been going on too long.",
      highlights: "Red barely touched in the brights (-0.032). The widest R-B gap of any LUT in the pack. Lit surfaces glow deep amber-gold. The light is excessive by design.",
      contrast: "Slightly compressed highlights — old film stock behaviour. The grade holds back the very brightest stops. Even excess has a ceiling.",
      saturation: "0.14 → 0.70. The highest in the pack. The colour is not just present — it is excessive. Which is entirely correct."
    }
  },
  {
    id: 16,
    name: "TRUE DETECTIVE — SEASON 1",
    year: "2014",
    mood: "Blue crushed in every zone · Louisiana heat",
    dp: "Adam Arkapaw, ACS",
    sections: {
      shadows: "Blue stripped near-zero. The deep darks read warm-brown — not the brown of wood or earth, but of air. The humidity is in the colour.",
      midtones: "Blue pulled hardest at -0.111, red and green nearly equal. The midtones are amber through and through. Skin reads warm without any romantic softness — just heat.",
      highlights: "The biggest blue extraction in the brights of any LUT in the pack: ΔB -0.119 in the cabinet. Where other grades leave the highlights cool, this one strips them. The light itself is warm. There is no blue sky anywhere in this world.",
      contrast: "Marginally brightened highlights. The grade doesn't crush — it bakes.",
      saturation: "0.13 → 0.38. The warmth isn't loud. It's ambient. Like weather."
    }
  },
  {
    id: 17,
    name: "SE7EN",
    year: "1995",
    mood: "Green injected into light · Red crushed · Highest contrast",
    dp: "Darius Khondji, ASC, AFC",
    sections: {
      shadows: "Not shifted. Not tinted. Erased. All three channels fall to absolute zero with perfect equality — the darkness here has no colour because it has no light left to colour. The abyss is complete.",
      midtones: "Red is removed hardest of any LUT in the pack — -0.107. Every surface that held warmth surrenders it. Skin flips green-dominant. Fabric reads cold. The midtones stop describing humanity and start describing something else.",
      highlights: "Green is actively added to the light sources. The cabinet glows. The only lamp in the room fights alone — the single amber practical in a world that has chosen green. It doesn't win.",
      contrast: "Blacks at zero. Highlights at 1.000. No graduation, no mercy. The most extreme tonal range in the SOLARIS pack.",
      saturation: "The highest saturation increase in the pack — driven entirely by green. More vivid, colder, more wrong. Khondji's paradox: the image becomes richer as it becomes less human."
    }
  },
  {
    id: 18,
    name: "PARASITE",
    year: "2019",
    mood: "Red extracted universally · Green dominant",
    dp: "Hong Kyung-pyo, KSC",
    sections: {
      shadows: "Red stripped. Green dominant even in the near-black zones. The darkness here has a cool biological quality — not cold, not warm, but the colour of filtered light through something man-made.",
      midtones: "Green leads by a clear margin: G:0.119, R:0.100, B:0.103. The midtones don't read warm or cool — they read controlled. The colour of a house that knows exactly what it's doing.",
      highlights: "Green barely moved at all — ΔG -0.001. It was already there. The light in this world is green because the surfaces are, because the architecture is, because the grade simply removes what was hiding it.",
      contrast: "Full range opened to 1.000. The grade reaches up into the brights and lets them breathe.",
      saturation: "0.13 → 0.37. Precise. The green is not aggressive — it's pervasive."
    }
  },
  {
    id: 19,
    name: "FIGHT CLUB",
    year: "1999",
    mood: "Desaturated grit · Green undertone · Urban decay",
    dp: "Jeff Cronenweth, ASC",
    sections: {
      shadows: "Muddy and polluted. The shadows carry a sickly green-brown that feels like nicotine stains on the walls of a basement apartment. Nothing is clean here.",
      midtones: "Desaturated and pushed toward green-yellow. Skin looks unhealthy on purpose — pale, sweaty, fluorescent-lit. The midtones reject any warmth that might make the world feel comfortable.",
      highlights: "Blown and harsh. Practicals clip into overexposure without the soft roll-off of traditional cinematography. The highlights feel institutional — like office lights or bare bulbs.",
      contrast: "Compressed in a way that flattens depth. The image feels like it's been photocopied — detail preserved but dimension removed. Deliberately ugly.",
      saturation: "Pulled way down but not absent. The remaining color is concentrated in greens and yellows — the palette of decay, of things left too long under bad lighting."
    }
  },
  {
    id: 20,
    name: "MOBLAND",
    year: "2025",
    mood: "Green from shadows · Blue from highlights · London amber",
    dp: "Baz Irvine, BSC, ISC",
    sections: {
      shadows: "Green stripped to near-zero. The darkness reads amber-brown — warm, low, the colour of a room that has been lived in by people who don't leave traces. Nothing cold survives down here.",
      midtones: "Green extracted hardest through the midrange, red spared most. Near the lamp, red is barely touched at -0.078. The practical light holds its warmth by default — the grade simply removes what was diluting it.",
      highlights: "Blue pulled hardest (-0.087), red almost untouched (-0.009). Lit surfaces hold at R:0.417 — deep amber-gold. The only warmth that was ever going to mean anything in this room is the lamp.",
      contrast: "Highlights gently extended. The grade reaches up without blowing out.",
      saturation: "0.13 → 0.46. Weighted, present, purposeful."
    }
  }
];

const faqData = [
  {
    question: "What software does this work with?",
    answer: "SOLARIS works in any software that accepts .cube LUT files — including Adobe Premiere Pro, DaVinci Resolve, Final Cut Pro X, Avid Media Composer, After Effects, Photoshop, and most professional NLEs and color tools."
  },
  {
    question: "Which folder should I use for my camera?",
    answer: "Each folder is labeled by camera system (Sony, Canon, RED, Blackmagic, ARRI, Standard Log). If your footage is already converted to Rec.709 or you're working in a normalized timeline, use the dedicated Rec.709 Main Looks folder. The included README walks through every scenario."
  },
  {
    question: "What's the difference between Creative LUTs and Utility LUTs?",
    answer: "Creative LUTs are the 20 signature cinematic looks — these are what you apply to achieve the visual style. Utility LUTs are the 101 technical conversion tools included in the pack: log-to-Rec.709 transforms for every major camera system (Sony, ARRI, RED, Blackmagic, Canon, Panasonic, DJI, Fujifilm, GoPro, Technicolor, and more), plus generic log utilities for mixed-format workflows."
  },
  {
    question: "Is this a one-time purchase?",
    answer: "Yes. $49 — no subscriptions, no license fees, no watermarks. Yours to use commercially on any production, forever."
  }
];

const cameraData = [
  { brand: "Sony", formats: "S-Log2 · S-Log3" },
  { brand: "ARRI", formats: "LogC" },
  { brand: "RED", formats: "IPP2 Log3G10 · REDLogFilm" },
  { brand: "Blackmagic", formats: "BMDFilm" },
  { brand: "Canon", formats: "C-Log · C-Log2 · C-Log3" },
  { brand: "Panasonic", formats: "V-Log · V-Log L" },
  { brand: "Apple", formats: "Apple Log" },
  { brand: "Standard", formats: "Log · Rec.709" }
];

// Film Grain Component
const FilmGrain = () => (
  <div className="film-grain">
    <svg>
      <filter id="noise">
        <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch"/>
        <feColorMatrix type="saturate" values="0"/>
      </filter>
      <rect width="100%" height="100%" filter="url(#noise)"/>
    </svg>
  </div>
);

// Navigation Component
const Navigation = () => {
  const scrollToBuy = () => {
    document.getElementById('buy')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToCameraPacks = () => {
    document.getElementById('camera-packs')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-6 py-3 sm:py-4"
      style={{ background: 'linear-gradient(to bottom, rgba(7,7,7,0.98), rgba(7,7,7,0.8), transparent)' }}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <a 
            href="https://www.skypanpictures.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
            data-testid="skypan-logo-link"
          >
            <img 
              src="/skypan-logo.png" 
              alt="Skypan Pictures" 
              width="200"
              height="60"
              className="h-8 sm:h-10 w-auto"
              fetchPriority="high"
              decoding="async"
            />
          </a>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={scrollToBuy}
            className="font-body uppercase text-[10px] sm:text-xs tracking-wider sm:tracking-widest font-semibold px-3 sm:px-6 py-2 sm:py-3 transition-all duration-300 hover:opacity-90 whitespace-nowrap"
            style={{ backgroundColor: 'var(--gold)', color: 'var(--black)' }}
            data-testid="nav-cta-button"
          >
            Get the Pack — $49
          </button>
        </div>
      </div>
    </nav>
  );
};

// Background image options
const HERO_BACKGROUNDS = {
  subtleAmberGlow: 'https://images.unsplash.com/photo-1765370847579-70d5d83401d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxzdWJ0bGUlMjBsaWdodCUyMGxlYWslMjBmaWxtJTIwZ3JhaW4lMjBhbWJlciUyMHNvZnQlMjBnbG93fGVufDB8fHx8MTc3MzQzMzkwNHww&ixlib=rb-4.1.0&q=85',
  duneHero1: 'https://static.prod-images.emergentagent.com/jobs/72c33184-d9ee-4aa9-abc2-722da5d8eb7b/images/9077a449093dd138b184b8cf5c1a3156c35df1d045e2d67ac850a032025849d1.png',
  duneHero2: 'https://static.prod-images.emergentagent.com/jobs/72c33184-d9ee-4aa9-abc2-722da5d8eb7b/images/637a59abcfb38d2151f89a7d1ed051c228581733277ea3a0f568ddeeaf233ebc.png',
  duneHero3: 'https://static.prod-images.emergentagent.com/jobs/72c33184-d9ee-4aa9-abc2-722da5d8eb7b/images/4de1cf51f9018dcddf446cd75dc013df4514343d7da4f215dd88e42a7781c021.png',
  unsplashField: 'https://images.unsplash.com/photo-1672234058959-951be5d848c1?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70&auto=format&ixlib=rb-4.1.0'
};

// Hero CTA — compact block that follows the video switcher.
// Wordmark + before/after slider + film list + spec line have been moved out;
// the video switcher above IS the visual proof, so we go straight to CTA.
// Background is inherited from the shared first-fold wrapper in MainPage —
// no local background so the amber glow flows through without a seam.
const Hero = () => {
  const scrollToBuy = () => {
    document.getElementById('buy')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="px-4 sm:px-6 py-6 sm:py-16 flex flex-col items-center text-center relative"
      style={{ backgroundColor: 'transparent' }}
      data-testid="hero-section"
    >
      <div className="relative z-10 flex flex-col items-center w-full">
        <button
          onClick={scrollToBuy}
          className="font-body uppercase text-sm tracking-widest font-semibold px-10 py-4 transition-all duration-300 hover:opacity-90 w-full sm:w-auto"
          style={{ backgroundColor: 'var(--gold)', color: 'var(--black)' }}
          data-testid="hero-cta-button"
        >
          Get the Pack — $49
        </button>

        <a
          href="#camera-packs"
          onClick={(e) => { e.preventDefault(); document.getElementById('camera-packs')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="font-mono uppercase mt-4 inline-flex items-center gap-2 transition-colors hover:text-white"
          style={{ color: 'var(--silver)', fontSize: '11px', letterSpacing: '0.2em' }}
          data-testid="hero-single-camera-link"
        >
          Only shoot one camera? Single camera packs — $24
        </a>

        <p
          className="font-body text-xs sm:text-sm mt-6 max-w-xl mx-auto"
          style={{ color: 'var(--muted)', lineHeight: 1.7 }}
          data-testid="hero-credibility"
        >
          Built by a working director with documentary credits on Amazon Prime and Apple TV.
          Not generated. Shot, graded, and tested on real cameras.
        </p>
      </div>
    </section>
  );
};

// LUT Card Component
const LutCard = ({ lut, onClick }) => (
  <div 
    className="lut-card p-5 cursor-pointer transition-all duration-300 group"
    style={{ backgroundColor: 'var(--near-black)' }}
    onClick={() => onClick(lut)}
    data-testid={`lut-card-${lut.id}`}
  >
    <span 
      className="font-mono text-xs block mb-2"
      style={{ color: 'var(--muted)' }}
    >
      {String(lut.id).padStart(2, '0')}
    </span>
    <h3 className="font-heading text-base mb-1 transition-colors duration-300 group-hover:text-[#f0ece4]" style={{ color: 'var(--gold)' }}>
      {lut.name} <span className="font-mono text-xs" style={{ color: 'var(--muted)' }}>{lut.year}</span>
    </h3>
    <p 
      className="font-mono text-xs uppercase tracking-wide"
      style={{ color: 'var(--muted)', fontSize: '10px' }}
    >
      {lut.mood}
    </p>
  </div>
);

// Modal Component
// Video URLs for LUTs that have preview videos
const lutVideoUrls = {
  "SICARIO": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7xq4i808_SICARIO.mp4",
  "BLADERUNNER": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/q6c3wc2a_BLADERUNNER.mp4",
  "SEVERANCE": "https://customer-assets.emergentagent.com/job_d5409ef9-1fe4-4af1-8789-30f72da7a2c4/artifacts/al4lh210_SEVERENCE.mp4",
  "KILLERS OF THE FLOWER MOON": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/99thhgv7_KILLERS%20OF%20THE%20FLOWER%20MOON.mp4",
  "THE BATMAN": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/iran8wqa_THE%20BATMAN.mp4",
  "DUNE: PART TWO": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/c57p3796_DUNE.mp4",
  "ZODIAC": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/6lygh439_ZODIAC.mp4",
  "THE REVENANT": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/374wjzf3_THE%20REVENENT.mp4",
  "PEAKY BLINDERS: THE IMMORTAL MAN": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7omxuswb_PEAKY.mp4",
  "GANGS OF NEW YORK": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/drg2y0rr_GANGS%20OF%20NY.mp4",
  "SUCCESSION — SEASON 2": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/epienkyr_SUCCESSION%20-%20Season%202.mp4",
  "GONE GIRL": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/ae49y7zr_GONE%20GIRL.mp4",
  "HEREDITARY": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/9ul8ceuc_HEREDITARY.mp4",
  "THE MATRIX": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/42vr51je_MATRIX.mp4",
  "BABYLON": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/dun9mpz6_BABYLON.mp4",
  "TRUE DETECTIVE — SEASON 1": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7otl4gs9_TRUE%20DETECTIVE.mp4",
  "SE7EN": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/pauaghaz_SE7EN.mp4",
  "PARASITE": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/kxvrcnd6_PARASITE.mp4",
  "FIGHT CLUB": "https://customer-assets.emergentagent.com/job_d5409ef9-1fe4-4af1-8789-30f72da7a2c4/artifacts/bp9fzfqt_FIGHT%20CLUB.mp4",
  "MOBLAND": "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/d49il5wh_MOBLAND.mp4",
};

const LutModal = ({ lut, onClose }) => {
  useEffect(() => {
    document.body.classList.add('modal-open');
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!lut) return null;

  const videoUrl = lutVideoUrls[lut.name];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      style={{ backgroundColor: 'rgba(7,7,7,0.92)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="lut-modal-backdrop"
    >
      <div 
        className="modal-content max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        style={{ 
          backgroundColor: 'var(--near-black)', 
          border: '1px solid var(--mid)' 
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lut-modal-content"
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-2 sm:top-4 right-2 sm:right-4 font-mono text-xs flex items-center gap-2 transition-colors duration-200 hover:text-[#c8a96e] z-10"
          style={{ color: 'var(--muted)' }}
          data-testid="modal-close-button"
        >
          <span>✕</span> <span className="hidden sm:inline">Close</span>
        </button>
        
        {/* Video Preview */}
        {videoUrl && (
          <div style={{ borderBottom: '1px solid var(--gold-dim)' }}>
            <div style={{ aspectRatio: '16/9', backgroundColor: '#000', maxHeight: '250px' }}>
              <video
                className="w-full h-full object-cover"
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
                data-testid="lut-modal-video"
              />
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="mb-4">
            <span className="font-mono text-xs block mb-1" style={{ color: 'var(--muted)' }}>
              {String(lut.id).padStart(2, '0')} / 20
            </span>
            <h2 className="font-heading text-xl sm:text-2xl mb-1" style={{ color: 'var(--white)' }}>
              {lut.name} <span className="text-sm sm:text-lg" style={{ color: 'var(--muted)' }}>({lut.year})</span>
            </h2>
            <p 
              className="font-mono text-xs uppercase tracking-widest"
              style={{ color: 'var(--gold)' }}
            >
              DP: {lut.dp}
            </p>
          </div>
          
          {/* Divider */}
          <div className="h-px w-full mb-4" style={{ backgroundColor: 'var(--gold-dim)' }}></div>
          
          {/* Sections */}
          <div className="space-y-4">
            {Object.entries(lut.sections).map(([key, value]) => (
              <div key={key}>
                <h4 
                  className="font-mono text-xs uppercase tracking-widest mb-1"
                  style={{ color: 'var(--gold)' }}
                >
                  {key}
                </h4>
                <p 
                  className="font-mono text-xs leading-relaxed"
                  style={{ color: 'var(--silver)', letterSpacing: '0.02em' }}
                >
                  {value}
                </p>
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--mid)' }}>
            <p 
              className="font-mono text-xs text-center uppercase tracking-widest"
              style={{ color: 'var(--muted)' }}
            >
              Part of the SOLARIS Collection
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// The 20 Looks Section
const LooksSection = () => {
  const [selectedLut, setSelectedLut] = useState(null);

  return (
    <section 
      className="py-24 px-6"
      style={{ backgroundColor: 'var(--black)' }}
      data-testid="looks-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <p 
            className="font-mono text-xs uppercase tracking-widest mb-4"
            style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
          >
            The Collection
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6" style={{ color: 'var(--white)' }}>
            20 <em>Signature</em> Looks
          </h2>
          <p 
            className="font-body text-sm sm:text-base max-w-3xl mx-auto leading-relaxed px-4 sm:px-0"
            style={{ color: 'var(--silver)' }}
          >
            Each LUT in SOLARIS is built from the color science of a specific film or series — pixel-analyzed, technically verified, and named only for works whose grades are genuinely distinct and universally recognized.
          </p>
        </div>
        
        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: 'var(--mid)' }}>
          {lutData.map((lut) => (
            <LutCard key={lut.id} lut={lut} onClick={setSelectedLut} />
          ))}
        </div>
      </div>
      
      {/* Modal */}
      {selectedLut && (
        <LutModal lut={selectedLut} onClose={() => setSelectedLut(null)} />
      )}
    </section>
  );
};

// Video Preview Section - Multi-clip showcase player
const VideoPreviewSection = () => {
  const [currentClip, setCurrentClip] = useState(0);
  // Autoplay on mount — the switcher is above-the-fold proof; muted playback
  // is allowed by all major browsers so we don't need a play-click gate.
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Dual-video crossfade: two <video> elements stacked. `activeSlot` names
  // which one is currently visible/playing. On clip change we load the NEW
  // clip into the INACTIVE slot, wait for `canplay`, start it playing, THEN
  // flip `activeSlot` so CSS opacity cross-dissolves between them. Because
  // the new video is already playing before it becomes visible, users never
  // see a still frame or a decoding pause.
  const [activeSlot, setActiveSlot] = useState(0);
  // Lazy-mount: only load the video (50-80MB) after it scrolls into view.
  // Massive LCP improvement on mobile — no video bytes fetched during initial paint.
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const sectionRef = React.useRef(null);
  const videoRefA = React.useRef(null);
  const videoRefB = React.useRef(null);
  const getActiveVideo = () => (activeSlot === 0 ? videoRefA.current : videoRefB.current);
  
  // Video clips data — desktop uses remote CDN, mobile uses local 720p (H.264)
  // 720p variants live in /public/videos/720p/ — massively smaller for cellular users.
  const clips = [
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/99thhgv7_KILLERS%20OF%20THE%20FLOWER%20MOON.mp4",
      poster: "/posters/killers.webp",
      label: "KILLERS OF THE FLOWER MOON",
      shortLabel: "KILLERS",
      year: "2023",
      dp: "Rodrigo Prieto, ASC, AMC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/q6c3wc2a_BLADERUNNER.mp4",
      srcMobile: "/videos/720p/BLADERUNNER_1.mp4",
      poster: "/posters/bladerunner.webp",
      label: "BLADE RUNNER",
      shortLabel: "BLADERUNNER",
      year: "1982",
      dp: "Jordan Cronenweth, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/dun9mpz6_BABYLON.mp4",
      srcMobile: "/videos/720p/BABYLON_1.mp4",
      poster: "/posters/babylon.webp",
      label: "BABYLON",
      shortLabel: "BABYLON",
      year: "2022",
      dp: "Linus Sandgren, FSF, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7omxuswb_PEAKY.mp4",
      srcMobile: "/videos/720p/PEAKY_1.mp4",
      poster: "/posters/peaky.webp",
      label: "PEAKY BLINDERS: THE IMMORTAL MAN",
      shortLabel: "PEAKY",
      year: "2025",
      dp: "Baz Irvine, BSC, ISC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7otl4gs9_TRUE%20DETECTIVE.mp4",
      srcMobile: "/videos/720p/TRUE%20DETECTIVE_1.mp4",
      poster: "/posters/truedetective.webp",
      label: "TRUE DETECTIVE — SEASON 1",
      shortLabel: "TRUE DETECTIVE",
      year: "2014",
      dp: "Adam Arkapaw, ACS",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/d49il5wh_MOBLAND.mp4",
      srcMobile: "/videos/720p/MOBLAND_1.mp4",
      poster: "/posters/mobland.webp",
      label: "MOBLAND",
      shortLabel: "MOBLAND",
      year: "2025",
      dp: "Baz Irvine, BSC, ISC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/42vr51je_MATRIX.mp4",
      srcMobile: "/videos/720p/MATRIX_1.mp4",
      poster: "/posters/matrix.webp",
      label: "THE MATRIX",
      shortLabel: "MATRIX",
      year: "1999",
      dp: "Bill Pope, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/6lygh439_ZODIAC.mp4",
      srcMobile: "/videos/720p/ZODIAC_1.mp4",
      poster: "/posters/zodiac.webp",
      label: "ZODIAC",
      shortLabel: "ZODIAC",
      year: "2007",
      dp: "Harris Savides, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/pauaghaz_SE7EN.mp4",
      srcMobile: "/videos/720p/SE7EN_1.mp4",
      poster: "/posters/se7en.webp",
      label: "SE7EN",
      shortLabel: "SE7EN",
      year: "1995",
      dp: "Darius Khondji, ASC, AFC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/c57p3796_DUNE.mp4",
      srcMobile: "/videos/720p/DUNE_1.mp4",
      poster: "/posters/dune.webp",
      label: "DUNE: PART TWO",
      shortLabel: "DUNE",
      year: "2024",
      dp: "Greig Fraser, ASC, ACS",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/0l4lczp7_FIGHT%20CLUB.mp4",
      srcMobile: "/videos/720p/FIGHT%20CLUB_1.mp4",
      poster: "/posters/se7en.webp",
      label: "FIGHT CLUB",
      shortLabel: "FIGHT CLUB",
      year: "1999",
      dp: "Jeff Cronenweth, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/drg2y0rr_GANGS%20OF%20NY.mp4",
      srcMobile: "/videos/720p/GANGS%20OF%20NY_1.mp4",
      poster: "/posters/babylon.webp",
      label: "GANGS OF NEW YORK",
      shortLabel: "GANGS OF NY",
      year: "2002",
      dp: "Michael Ballhaus, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/ae49y7zr_GONE%20GIRL.mp4",
      srcMobile: "/videos/720p/GONE%20GIRL_1.mp4",
      poster: "/posters/zodiac.webp",
      label: "GONE GIRL",
      shortLabel: "GONE GIRL",
      year: "2014",
      dp: "Jeff Cronenweth, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/9ul8ceuc_HEREDITARY.mp4",
      srcMobile: "/videos/720p/HEREDITARY_1.mp4",
      poster: "/posters/matrix.webp",
      label: "HEREDITARY",
      shortLabel: "HEREDITARY",
      year: "2018",
      dp: "Pawel Pogorzelski",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/kxvrcnd6_PARASITE.mp4",
      srcMobile: "/videos/720p/PARASITE_1.mp4",
      poster: "/posters/mobland.webp",
      label: "PARASITE",
      shortLabel: "PARASITE",
      year: "2019",
      dp: "Hong Kyung-pyo",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_d5409ef9-1fe4-4af1-8789-30f72da7a2c4/artifacts/uewuvxbq_SEVERENCE.mp4",
      srcMobile: "/videos/720p/SEVERENCE_1.mp4",
      poster: "/posters/truedetective.webp",
      label: "SEVERANCE",
      shortLabel: "SEVERANCE",
      year: "2022",
      dp: "Jessica Lee Gagné, CSC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/7xq4i808_SICARIO.mp4",
      srcMobile: "/videos/720p/SICARIO_1.mp4",
      poster: "/posters/sicario.webp",
      label: "SICARIO",
      shortLabel: "SICARIO",
      year: "2015",
      dp: "Roger Deakins, CBE ASC BSC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/epienkyr_SUCCESSION%20-%20Season%202.mp4",
      srcMobile: "/videos/720p/SUCCESSION%20-%20Season%202_1.mp4",
      poster: "/posters/peaky.webp",
      label: "SUCCESSION — SEASON 2",
      shortLabel: "SUCCESSION",
      year: "2019",
      dp: "Andrij Parekh, ASC",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/iran8wqa_THE%20BATMAN.mp4",
      srcMobile: "/videos/720p/THE%20BATMAN_1.mp4",
      poster: "/posters/bladerunner.webp",
      label: "THE BATMAN",
      shortLabel: "THE BATMAN",
      year: "2022",
      dp: "Greig Fraser, ASC, ACS",
      logFormat: "Standard Log"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_72c33184-d9ee-4aa9-abc2-722da5d8eb7b/artifacts/374wjzf3_THE%20REVENENT.mp4",
      srcMobile: "/videos/720p/THE%20REVENENT_1.mp4",
      poster: "/posters/dune.webp",
      label: "THE REVENANT",
      shortLabel: "REVENANT",
      year: "2015",
      dp: "Emmanuel Lubezki, AMC, ASC",
      logFormat: "Standard Log"
    },
  ];

  const currentClipData = clips[currentClip];

  // IntersectionObserver — only mount the video element (and start fetching bytes)
  // once user scrolls the video section INTO view. Saves 50-80MB on first paint.
  useEffect(() => {
    if (shouldLoadVideo || !sectionRef.current || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShouldLoadVideo(true); obs.disconnect(); } },
      { rootMargin: "0px", threshold: 0.1 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [shouldLoadVideo]);

  // Attach playback event listeners to whichever <video> is currently active.
  // Re-runs when the active slot flips after a crossfade so time/duration/ended
  // events fire against the newly-visible video.
  useEffect(() => {
    if (!shouldLoadVideo) return;
    const video = getActiveVideo();
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleEnded = () => {
      // Auto-advance — crossfade effect handles the transition.
      setCurrentClip((prev) => (prev + 1) % clips.length);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
    };
  }, [activeSlot, shouldLoadVideo, clips.length]);

  // Clip-change effect — the seamless crossfade engine.
  // On mount + every currentClip change:
  //   1. Pick the INACTIVE slot (the video not currently visible).
  //   2. Set its src to the new clip, call load().
  //   3. When `canplay` fires, start it playing then flip activeSlot →
  //      CSS opacity transition cross-dissolves the old video out and the
  //      new (already-playing) video in. No still frame, no decoding gap.
  //   4. After the fade finishes, pause the now-hidden old video.
  useEffect(() => {
    if (!shouldLoadVideo) return;
    const nextSlot = 1 - activeSlot;
    const nextVideo = nextSlot === 0 ? videoRefA.current : videoRefB.current;
    if (!nextVideo) return;

    const useMobile = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(max-width: 768px)').matches;
    const nextSrc = useMobile && currentClipData.srcMobile
      ? currentClipData.srcMobile
      : currentClipData.src;
    if (currentClipData.poster) nextVideo.poster = currentClipData.poster;

    let cancelled = false;
    const CROSSFADE_MS = 500;

    const onCanPlay = () => {
      if (cancelled) return;
      nextVideo.muted = true; // muted autoplay is always allowed
      const playPromise = nextVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
      // Flip visible slot — CSS opacity transition (see JSX) handles the
      // cross-dissolve. The new video is already playing so users see
      // continuous motion, not a still frame.
      setActiveSlot(nextSlot);
      // Prime duration + currentTime here since `loadedmetadata` may have
      // already fired BEFORE the listener-effect re-binds to the new slot,
      // which would leave the progress bar frozen at 0%.
      if (isFinite(nextVideo.duration)) setDuration(nextVideo.duration);
      setCurrentTime(nextVideo.currentTime || 0);
      // After the crossfade completes, pause the OLD video to save CPU/battery.
      const prevVideo = nextSlot === 0 ? videoRefB.current : videoRefA.current;
      setTimeout(() => {
        if (!cancelled && prevVideo && prevVideo !== nextVideo) {
          try { prevVideo.pause(); } catch (e) { /* ignore */ }
        }
      }, CROSSFADE_MS + 50);
    };
    nextVideo.addEventListener('canplay', onCanPlay, { once: true });

    nextVideo.src = nextSrc;
    nextVideo.load();

    // Failsafe: if canplay never fires (bad network), swap anyway after 4s so
    // the user isn't stuck watching the old clip forever.
    const failsafe = setTimeout(() => {
      if (!cancelled) onCanPlay();
    }, 4000);

    return () => {
      cancelled = true;
      nextVideo.removeEventListener('canplay', onCanPlay);
      clearTimeout(failsafe);
    };
  }, [currentClip, shouldLoadVideo, currentClipData.src, currentClipData.srcMobile, currentClipData.poster]);

  const togglePlay = () => {
    const video = getActiveVideo();
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const video = getActiveVideo();
    if (!video) return;
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const goToClip = (index) => {
    if (index === currentClip) return;
    // Crossfade effect above handles the entire visual transition —
    // no need to touch any transition flag here.
    setCurrentClip(index);
  };

  const handleProgressClick = (e) => {
    const video = getActiveVideo();
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * duration;
  };

  const toggleFullscreen = () => {
    const container = document.querySelector('[data-video-container]');
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const goToPrev = () => {
    goToClip((currentClip - 1 + clips.length) % clips.length);
  };

  const goToNext = () => {
    goToClip((currentClip + 1) % clips.length);
  };

  return (
    <section 
      ref={sectionRef}
      className="py-6 sm:py-24 px-6 relative"
      style={{ backgroundColor: 'transparent' }}
      data-testid="video-preview-section"
    >
      <div className="relative z-10">
      <div className="section-divider mb-6 sm:mb-24"></div>
      
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-6 sm:mb-16">
          <p 
            className="font-mono text-xs uppercase tracking-widest mb-2 sm:mb-4"
            style={{ color: 'var(--gold)', letterSpacing: '0.25em', fontSize: '10px' }}
          >
            See It In Action
          </p>
          <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl mb-2 sm:mb-3" style={{ color: 'var(--white)' }}>
            From LOG. <em>To Cinema.</em>
          </h2>
          <p 
            className="font-heading italic text-base sm:text-lg mb-0 sm:mb-6"
            style={{ color: 'var(--silver)' }}
          >
            Ten looks. Ten worlds.
          </p>
          {/* Long description hidden on mobile — kills a full viewport of dead space
              above the video. Desktop still gets the full context. */}
          <p 
            className="hidden sm:block font-body text-sm sm:text-base max-w-xl mx-auto leading-relaxed px-4 sm:px-0"
            style={{ color: 'var(--silver)' }}
          >
            Ungraded log footage is a blank canvas — flat, lifeless, waiting. What follows is what SOLARIS does to it.
          </p>
        </div>
        
        {/* Video Player Container */}
        <div className="max-w-5xl mx-auto" data-video-container>
          {/* Video Frame with 2.39:1 Aspect Ratio */}
          <div 
            className="relative w-full overflow-hidden"
            style={{ 
              aspectRatio: '2.39/1',
              backgroundColor: '#000000',
              backgroundImage: currentClipData.poster ? `url(${currentClipData.poster})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              border: '1px solid var(--gold)'
            }}
          >
            {/* Dual-video crossfade — two stacked <video> elements. The one
                matching `activeSlot` is at opacity 1 (visible + playing); the
                other holds the previous clip at opacity 0 (paused after fade
                finishes). On clip change we load the new src into the invisible
                slot, wait for canplay + play, THEN flip activeSlot — CSS
                opacity transition cross-dissolves. Truly no still frame. */}
            {shouldLoadVideo && (
              <>
                <video
                  ref={videoRefA}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                  style={{ opacity: activeSlot === 0 ? 1 : 0 }}
                  muted
                  autoPlay
                  playsInline
                  preload="auto"
                  data-testid={activeSlot === 0 ? 'video-player' : 'video-player-b'}
                />
                <video
                  ref={videoRefB}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                  style={{ opacity: activeSlot === 1 ? 1 : 0 }}
                  muted
                  autoPlay
                  playsInline
                  preload="auto"
                  data-testid={activeSlot === 1 ? 'video-player' : 'video-player-a'}
                />
              </>
            )}
            
            {/* Top-left branding overlay */}
            <div 
              className="absolute top-4 left-4 transition-opacity duration-300"
              style={{ opacity: isPlaying ? 1 : 0.5 }}
            >
              <p 
                className="font-mono text-xs"
                style={{ color: 'var(--subtle)', fontSize: '9px', letterSpacing: '0.15em' }}
              >
                SOLARIS — Cinematic LUT Library
              </p>
            </div>
            
            {/* Bottom-left info overlay */}
            <div 
              className="absolute bottom-3 sm:bottom-6 left-3 sm:left-6 transition-opacity duration-500"
              style={{ opacity: 1 }}
            >
              <p 
                className="font-mono uppercase mb-1 hidden sm:block"
                style={{ color: 'var(--gold-dim)', fontSize: '9px', letterSpacing: '0.25em' }}
              >
                {currentClipData.logFormat}
              </p>
              <p 
                className="font-heading text-sm sm:text-xl md:text-2xl mb-1"
                style={{ color: 'var(--white)' }}
              >
                {currentClipData.label}
              </p>
              <p 
                className="font-mono hidden md:block"
                style={{ color: 'var(--muted)', fontSize: '10px' }}
              >
                {currentClipData.year} · {currentClipData.dp}
              </p>
            </div>
            
            {/* Bottom-right progress dots */}
            <div className="absolute bottom-3 sm:bottom-6 right-3 sm:right-6 flex gap-1 sm:gap-2">
              {clips.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToClip(idx)}
                  className="rounded-full transition-all duration-300 hover:scale-125"
                  style={{
                    width: currentClip === idx ? '8px' : '6px',
                    height: currentClip === idx ? '8px' : '6px',
                    backgroundColor: currentClip === idx ? 'var(--gold)' : 'transparent',
                    border: currentClip === idx ? 'none' : '1px solid var(--subtle)',
                  }}
                  data-testid={`clip-dot-${idx}`}
                />
              ))}
            </div>

            {/* Navigation arrows */}
            <button
              onClick={goToPrev}
              className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 p-1 sm:p-2 transition-all hover:scale-110"
              style={{ color: 'var(--silver)' }}
              data-testid="video-prev-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="sm:w-6 sm:h-6">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 p-1 sm:p-2 transition-all hover:scale-110"
              style={{ color: 'var(--silver)' }}
              data-testid="video-next-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="sm:w-6 sm:h-6">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          
          {/* Custom Controls Bar */}
          <div 
            className="flex items-center gap-2 sm:gap-4 px-2 sm:px-4 h-10 sm:h-11"
            style={{ 
              backgroundColor: 'var(--near-black)',
              borderTop: '1px solid var(--gold)'
            }}
          >
            {/* Play/Pause */}
            <button 
              onClick={togglePlay}
              className="hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="video-play-btn"
            >
              {isPlaying ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              )}
            </button>
            
            {/* Time Display */}
            <span 
              className="font-mono text-xs hidden md:block"
              style={{ color: 'var(--muted)', fontSize: '10px', minWidth: '80px' }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            
            {/* Progress Bar */}
            <div 
              className="flex-1 h-0.5 cursor-pointer relative group"
              style={{ backgroundColor: 'var(--mid)' }}
              onClick={handleProgressClick}
              data-testid="video-progress-bar"
            >
              <div 
                className="h-full transition-all"
                style={{ 
                  width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
                  backgroundColor: 'var(--gold)'
                }}
              />
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ 
                  left: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
                  backgroundColor: 'var(--gold)',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            </div>
            
            {/* Clip indicator */}
            <span 
              className="font-mono text-xs hidden md:block"
              style={{ color: 'var(--gold-dim)', fontSize: '10px' }}
            >
              {currentClip + 1} / {clips.length}
            </span>
            
            {/* Mute Toggle */}
            <button 
              onClick={toggleMute}
              className="hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="video-mute-btn"
            >
              {isMuted ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
            
            {/* Fullscreen */}
            <button 
              onClick={toggleFullscreen}
              className="hover:text-[#c8a96e] transition-colors hidden md:block"
              style={{ color: 'var(--silver)' }}
              data-testid="video-fullscreen-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          </div>
          
          {/* Segment Labels Strip */}
          <div 
            className="flex overflow-x-auto py-3 gap-4 md:justify-center scrollbar-hide"
            style={{ 
              backgroundColor: 'var(--near-black)',
              borderBottom: '1px solid var(--subtle)'
            }}
          >
            {clips.map((clip, idx) => (
              <button
                key={idx}
                onClick={() => goToClip(idx)}
                className="font-mono uppercase whitespace-nowrap px-2 transition-colors hover:text-[#c8a96e] flex-shrink-0"
                style={{ 
                  fontSize: '9px', 
                  letterSpacing: '0.15em',
                  color: currentClip === idx ? 'var(--gold)' : 'var(--muted)'
                }}
                data-testid={`segment-label-${idx}`}
              >
                {clip.shortLabel}
              </button>
            ))}
          </div>
        </div>
        
        {/* Copy Below Player */}
        <div className="text-center mt-10">
          <p 
            className="font-heading italic text-lg mb-2"
            style={{ color: 'var(--silver)' }}
          >
            Every transition in this video is a real export from the SOLARIS pack. Minor adjustments explained in the README.
          </p>
          <p 
            className="font-mono uppercase"
            style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.2em' }}
          >
            Footage shot on Sony FX3. S-Log3 input version shown.
          </p>
        </div>
      </div>
      </div>
      
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

// Camera Compatibility Section
const CameraSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="camera-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          Compatibility
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl mb-6" style={{ color: 'var(--white)' }}>
          Every LUT. <em>Every</em> Camera.
        </h2>
        <p 
          className="font-body text-base max-w-2xl mx-auto"
          style={{ color: 'var(--silver)' }}
        >
          Each look ships with input-specific versions covering every major camera system and log format. One pack. Your entire workflow.
        </p>
      </div>
      
      {/* Camera Grid */}
      <div 
        className="grid grid-cols-2 md:grid-cols-4 gap-px"
        style={{ backgroundColor: 'var(--mid)' }}
      >
        {cameraData.map((camera, index) => (
          <div 
            key={index}
            className="p-6 text-center"
            style={{ backgroundColor: 'var(--near-black)' }}
            data-testid={`camera-${camera.brand.toLowerCase()}`}
          >
            <h3 
              className="font-heading text-lg mb-2"
              style={{ color: 'var(--white)' }}
            >
              {camera.brand}
            </h3>
            <p 
              className="font-mono text-xs"
              style={{ color: 'var(--gold-dim)', fontSize: '10px' }}
            >
              {camera.formats}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// What's Included Section
const IncludedSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="included-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          What's Included
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl mb-6" style={{ color: 'var(--white)' }}>
          A Complete <em>Professional</em> Library
        </h2>
        <p 
          className="font-body text-base max-w-2xl mx-auto"
          style={{ color: 'var(--silver)' }}
        >
          Not a random folder of files. A carefully organized LUT system built for production use.
        </p>
      </div>
      
      {/* 2x2 Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Creative Looks */}
        <div 
          className="p-8 transition-colors duration-300 hover:bg-[#161616]"
          style={{ backgroundColor: 'var(--near-black)', borderBottom: '1px solid var(--mid)' }}
        >
          <h3 className="font-heading text-2xl mb-4" style={{ color: 'var(--gold)' }}>
            Creative Looks
          </h3>
          <ul className="space-y-2 font-body text-sm" style={{ color: 'var(--light)' }}>
            <li>• 20 signature cinematic looks in .cube format</li>
            <li>• Input-specific versions for every major camera family</li>
            <li>• Rec.709 versions for already-normalized footage</li>
            <li>• Universal .cube format for all NLEs</li>
            <li>• Each look refined across hundreds of real productions</li>
          </ul>
        </div>
        
        {/* Utility LUTs */}
        <div 
          className="p-8 transition-colors duration-300 hover:bg-[#161616]"
          style={{ backgroundColor: 'var(--near-black)', borderBottom: '1px solid var(--mid)' }}
        >
          <h3 className="font-heading text-2xl mb-4" style={{ color: 'var(--gold)' }}>
            101 Utility & Conversion LUTs
          </h3>
          <div className="font-mono text-xs leading-relaxed" style={{ color: 'var(--silver)', fontSize: '11px' }}>
            Alexa Log-C → Rec.709 · BMDFilm 4K → Alexa Log-C · BMDFilm BMCC, BMPC, BMPCC, URSA, URSA 4.6K → Rec.709 · Canon C-Log → Rec.709 · DJI D-Log → Rec.709 · Fujifilm F-Log → Rec.709 · GoPro Protune → Video · Panasonic Cine-D → Rec.709 · Panasonic V-Log → Rec.709 · REDlog → Rec.709 · REDlogFilm → Rec.709 · Sony S-Log2 → Rec.709 · Sony S-Log3 → Rec.709 · Technicolor CineStyle → Rec.709 · Generic LOG → Rec.709 · Generic Rec.709 → LOG · Generic Rec.709 → Alexa Log-C · Divers
          </div>
        </div>
        
        {/* Rec.709 Main Looks */}
        <div 
          className="p-8 transition-colors duration-300 hover:bg-[#161616]"
          style={{ backgroundColor: 'var(--near-black)', borderBottom: '1px solid var(--mid)' }}
        >
          <h3 className="font-heading text-2xl mb-4" style={{ color: 'var(--gold)' }}>
            Rec.709 Main Looks
          </h3>
          <ul className="space-y-2 font-body text-sm" style={{ color: 'var(--light)' }}>
            <li>• Dedicated versions for color-managed workflows</li>
            <li>• Safe to apply on footage already converted to Rec.709</li>
            <li>• No blown contrast, no runaway saturation</li>
            <li>• Essential for editors working in normalized timelines</li>
          </ul>
        </div>
        
        {/* Documentation */}
        <div 
          className="p-8 transition-colors duration-300 hover:bg-[#161616]"
          style={{ backgroundColor: 'var(--near-black)', borderBottom: '1px solid var(--mid)' }}
        >
          <h3 className="font-heading text-2xl mb-4" style={{ color: 'var(--gold)' }}>
            Documentation
          </h3>
          <ul className="space-y-2 font-body text-sm" style={{ color: 'var(--light)' }}>
            <li>• Full README with folder navigation guide</li>
            <li>• Explains when to use log vs. Rec.709 versions</li>
            <li>• Installation guide for Premiere, Resolve, FCPX, Avid</li>
            <li>• Workflow recommendations per camera system</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// File Formats Section
const FormatsSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="formats-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          File Formats
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl mb-3" style={{ color: 'var(--white)' }}>
          Built for <em>Every</em> NLE
        </h2>
        <p className="font-body text-base" style={{ color: 'var(--silver)' }}>
          Universal format. Maximum compatibility.
        </p>
      </div>
      
      {/* Format Cards */}
      <div className="max-w-2xl mx-auto space-y-6">
        {/* .cube */}
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)', borderBottom: '2px solid var(--gold)' }}
        >
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-mono text-2xl" style={{ color: 'var(--white)' }}>.cube</h3>
            <span 
              className="font-mono text-xs px-3 py-1"
              style={{ backgroundColor: 'var(--gold)', color: 'var(--black)' }}
            >
              Primary Format
            </span>
          </div>
          <p className="font-body text-sm" style={{ color: 'var(--silver)' }}>
            The industry standard. Works in Adobe Premiere Pro, DaVinci Resolve, Final Cut Pro, AVID Media Composer, and virtually every professional color tool.
          </p>
        </div>
      
        {/* 65³ */}
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)', borderLeft: '2px solid var(--amber)' }}
        >
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-mono text-2xl" style={{ color: 'var(--white)' }}>65³</h3>
            <span 
              className="font-mono text-xs px-3 py-1"
              style={{ backgroundColor: 'var(--amber)', color: 'var(--black)' }}
            >
              Coming Soon
            </span>
          </div>
          <p className="font-body text-sm" style={{ color: 'var(--silver)' }}>
            High-resolution 65×65×65 .cube versions are in development as a premium upgrade — offering smoother interpolation and cleaner gradient performance for demanding workflows.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Folder Structure Section
const FolderSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="folder-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          Organization
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl" style={{ color: 'var(--white)' }}>
          Folder <em>Structure</em>
        </h2>
      </div>
      
      {/* Terminal Box */}
      <div 
        className="p-8 font-mono text-sm leading-relaxed"
        style={{ backgroundColor: 'var(--near-black)', border: '1px solid var(--mid)' }}
      >
        <pre style={{ color: 'var(--silver)' }}>
          <span style={{ color: 'var(--gold)' }}>📁</span> SOLARIS — Cinematic LUT Library{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 01 — Standard Log{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 02 — Sony  <span style={{ color: 'var(--muted)' }}>(S-Log2 · S-Log3)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 03 — RED  <span style={{ color: 'var(--muted)' }}>(IPP2 · REDLogFilm)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 04 — Blackmagic  <span style={{ color: 'var(--muted)' }}>(BMDFilm)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 05 — Canon  <span style={{ color: 'var(--muted)' }}>(C-Log · C-Log2 · C-Log3)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 06 — ARRI  <span style={{ color: 'var(--muted)' }}>(LogC)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 07 — Panasonic  <span style={{ color: 'var(--muted)' }}>(V-Log · V-Log L)</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 08 — Rec.709 Main Looks{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 09 — Utility & Conversion LUTs  <span style={{ color: 'var(--muted)' }}>← 101 LUTs across 22 categories</span>{'\n'}
          ├── <span style={{ color: 'var(--gold)' }}>📁</span> 10 — Installation Guide{'\n'}
          └── <span style={{ color: 'var(--gold)' }}>📄</span> README.pdf
        </pre>
      </div>
    </div>
  </section>
);

// Who It's For Section
const AudienceSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="audience-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          Who It's For
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl" style={{ color: 'var(--white)' }}>
          Built for <em>Working</em> Filmmakers
        </h2>
      </div>
      
      {/* Three Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)', borderTop: '2px solid var(--gold)' }}
        >
          <span className="font-mono text-xs block mb-4" style={{ color: 'var(--gold-dim)' }}>01</span>
          <h3 className="font-heading text-xl mb-4" style={{ color: 'var(--white)' }}>
            Editors & Colorists
          </h3>
          <p className="font-body text-sm" style={{ color: 'var(--silver)' }}>
            You need reliable, fast looks that hold up under scrutiny. SOLARIS gives you a production-tested starting point — not a preset dump.
          </p>
        </div>
        
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)', borderTop: '2px solid var(--gold)' }}
        >
          <span className="font-mono text-xs block mb-4" style={{ color: 'var(--gold-dim)' }}>02</span>
          <h3 className="font-heading text-xl mb-4" style={{ color: 'var(--white)' }}>
            DPs & Cinematographers
          </h3>
          <p className="font-body text-sm" style={{ color: 'var(--silver)' }}>
            Use the camera-specific input families to accurately preview your grade on set or in review. Works with your exact camera system.
          </p>
        </div>
        
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)', borderTop: '2px solid var(--gold)' }}
        >
          <span className="font-mono text-xs block mb-4" style={{ color: 'var(--gold-dim)' }}>03</span>
          <h3 className="font-heading text-xl mb-4" style={{ color: 'var(--white)' }}>
            Content Creators
          </h3>
          <p className="font-body text-sm" style={{ color: 'var(--silver)' }}>
            You've converted your footage to Rec.709 and need cinematic looks that don't crush your image. The dedicated Rec.709 folder was built for you.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Brand Story Section
const StorySection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="story-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Text */}
        <div>
          <p 
            className="font-mono text-xs uppercase tracking-widest mb-4"
            style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
          >
            The Story
          </p>
          <h2 className="font-heading text-4xl mb-8" style={{ color: 'var(--white)' }}>
            Built Inside <em>Real</em> Productions
          </h2>
          <p className="font-body text-base leading-relaxed" style={{ color: 'var(--silver)' }}>
            SOLARIS was built inside working productions — documentaries delivered to Amazon Prime and Apple TV, branded work, and a decade of color grading real footage from real cameras. Every look in this pack has shipped on a professional timeline. These are not presets made for a marketplace. They are the tools I grade with.
          </p>
        </div>
        
        {/* Credentials */}
        <div 
          className="p-8"
          style={{ backgroundColor: 'var(--near-black)' }}
        >
          <h4 
            className="font-mono text-xs uppercase tracking-widest mb-6"
            style={{ color: 'var(--gold)' }}
          >
            Production Credits
          </h4>
          <ul className="space-y-4">
            {[
              "Documentary work on Amazon Prime Video",
              "Distributed via Apple TV",
              "Distributed via Tubi",
              "Award-winning productions · International distribution",
              "10+ years of color science refinement in professional production"
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span style={{ color: 'var(--gold)' }}>—</span>
                <span className="font-mono text-xs" style={{ color: 'var(--silver)', fontSize: '11px' }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      className="py-24 px-6"
      style={{ backgroundColor: 'var(--black)' }}
      data-testid="faq-section"
    >
      <div className="section-divider mb-24"></div>
      
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <p 
            className="font-mono text-xs uppercase tracking-widest mb-4"
            style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
          >
            Questions
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl" style={{ color: 'var(--white)' }}>
            <em>Frequently</em> Asked
          </h2>
        </div>
        
        {/* Accordion */}
        <div className="space-y-0">
          {faqData.map((faq, index) => (
            <div 
              key={index}
              className="border-b"
              style={{ borderColor: 'var(--mid)' }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full py-6 flex items-center justify-between text-left transition-colors duration-200 hover:text-[#c8a96e]"
                style={{ color: 'var(--white)' }}
                data-testid={`faq-question-${index}`}
              >
                <span className="font-body text-base pr-8">{faq.question}</span>
                <span 
                  className="font-mono text-xl flex-shrink-0 transition-transform duration-200"
                  style={{ color: 'var(--gold)', transform: openIndex === index ? 'rotate(45deg)' : 'none' }}
                >
                  +
                </span>
              </button>
              <div 
                className={`faq-answer ${openIndex === index ? 'open pb-6' : ''}`}
              >
                <p 
                  className="font-body text-sm leading-relaxed"
                  style={{ color: 'var(--silver)' }}
                >
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Buy Section
const BuySection = ({ idSuffix = "" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(null);

  const handleBuyFullPack = async () => {
    // Suppress the global bubble-phase AddToCart listener for this click.
    markCheckoutIntent();
    setIsLoading(true);
    setLoadingProduct('full-pack');
    
    try {
      const response = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: 'full-pack',
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        // Fire Meta Pixel InitiateCheckout BEFORE redirect, with 250ms
        // buffer so the pixel POST isn't killed by page unload.
        trackInitiateCheckout('full-pack');
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 250);
        return;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingProduct(null);
    }
  };

  const [showCameraPacks, setShowCameraPacks] = React.useState(false);
  const buySectionRef = React.useRef(null);

  // ViewContent — fire once when the buy section scrolls into view.
  // This is Meta's mid-funnel signal ("saw the product"), separate from PageView.
  React.useEffect(() => {
    const el = buySectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackViewContent('full-pack');
          obs.disconnect();
        }
      },
      { rootMargin: '0px', threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
  <section 
    ref={buySectionRef}
    id={idSuffix ? undefined : "buy"}
    className="py-24 md:py-32 px-6"
    style={{ backgroundColor: 'var(--near-black)' }}
    data-testid={`buy-section${idSuffix}`}
  >
    <div className="max-w-2xl mx-auto text-center">
      {/* HERO Full Pack Card — primary CTA, no side-by-side competing choice */}
      <div className="p-8 sm:p-10 mb-6 relative overflow-hidden" style={{ backgroundColor: 'var(--dark)', border: '2px solid var(--gold)' }} data-testid="buy-full-pack-card">
        {/* Badge */}
        <div className="absolute top-0 right-0 px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest" style={{ backgroundColor: 'var(--gold)', color: 'var(--black)', letterSpacing: '0.25em' }}>
          Best Value &mdash; Save $30
        </div>

        <p className="font-mono text-xs uppercase tracking-widest mb-3 mt-2" style={{ color: 'var(--gold)', letterSpacing: '0.25em' }}>
          Complete Library
        </p>
        <h3 className="font-heading text-3xl sm:text-4xl mb-4" style={{ color: 'var(--white)' }}>
          The Full SOLARIS Pack
        </h3>

        {/* Enhanced price anchor */}
        <div className="flex items-baseline justify-center gap-3 mb-2">
          <span className="font-heading text-2xl" style={{ color: 'var(--muted)', textDecoration: 'line-through' }}>$79</span>
          <span className="font-heading text-6xl sm:text-7xl" style={{ color: 'var(--gold)', letterSpacing: '-1px' }}>$49</span>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest mb-6" style={{ color: 'var(--gold-dim)', letterSpacing: '0.25em' }}>
          One-time purchase &middot; No subscriptions
        </p>

        <ul className="text-sm space-y-2.5 mb-8 text-left max-w-md mx-auto" style={{ color: 'var(--silver)', fontFamily: "'Montserrat', sans-serif" }}>
          <li>&#10003; All 20 cinematic looks &mdash; Sicario, Blade Runner, Dune, Peaky Blinders + 16 more</li>
          <li>&#10003; All 8 camera families &mdash; Sony, ARRI, RED, Canon, Blackmagic, Panasonic, Apple Log, Rec.709</li>
          <li>&#10003; 101 utility & log-to-Rec.709 conversion LUTs</li>
          <li>&#10003; Works in DaVinci Resolve, Premiere Pro, Final Cut Pro, After Effects</li>
          <li>&#10003; Instant download &middot; Yours forever</li>
        </ul>

        <button
          onClick={handleBuyFullPack}
          disabled={isLoading}
          className="inline-block w-full font-body uppercase text-sm tracking-widest font-semibold px-8 py-5 transition-all duration-300 hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: 'var(--gold)', color: 'var(--black)', letterSpacing: '0.2em' }}
          data-testid="buy-full-pack-btn"
        >
          {loadingProduct === 'full-pack' ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin"></span>
              Processing&hellip;
            </span>
          ) : (
            <>Get the Full Pack &mdash; $49</>
          )}
        </button>
        <p className="font-body text-xs mt-3" style={{ color: 'var(--muted)' }}>
          Secure checkout via Stripe &middot; Instant download after payment
        </p>
      </div>

      {/* Secondary: shop by camera (text link — no visual competition) */}
      <div id={idSuffix ? undefined : "camera-packs"} className="mt-8" data-testid="buy-camera-toggle-wrap">
        {!showCameraPacks ? (
          <button
            onClick={() => setShowCameraPacks(true)}
            className="font-body text-sm underline underline-offset-4 transition-colors hover:text-white"
            style={{ color: 'var(--muted)' }}
            data-testid="buy-show-camera-packs"
          >
            Only shoot one camera? See single-camera packs &mdash; $24 &rarr;
          </button>
        ) : (
          <div className="p-6 sm:p-8 text-left" style={{ backgroundColor: 'var(--dark)', border: '1px solid var(--mid)' }}>
            <div className="flex items-baseline justify-between mb-3">
              <p className="font-mono text-xs uppercase tracking-widest" style={{ color: 'var(--gold-dim)', letterSpacing: '0.25em' }}>
                Single Camera &mdash; $24
              </p>
              <button onClick={() => setShowCameraPacks(false)} className="font-mono text-xs" style={{ color: 'var(--muted)' }}>&times; close</button>
            </div>
            <p className="font-body text-sm mb-4" style={{ color: 'var(--silver)' }}>
              All 20 looks for one camera family. Perfect if you shoot exclusively on one system.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { name: "Sony", path: "/sony" },
                { name: "ARRI", path: "/arri" },
                { name: "RED", path: "/red" },
                { name: "Canon", path: "/canon" },
                { name: "Blackmagic", path: "/blackmagic" },
                { name: "Panasonic", path: "/panasonic" },
                { name: "Apple", path: "/apple" },
                { name: "Standard", path: "/standard" }
              ].map((camera) => (
                <a
                  key={camera.name}
                  href={camera.path}
                  className="font-body text-xs tracking-wide py-2 px-3 transition-all duration-300 border hover:bg-[#c8a96e] hover:text-black hover:border-[#c8a96e] text-center"
                  style={{ borderColor: 'var(--mid)', color: 'var(--silver)' }}
                  data-testid={`buy-camera-${camera.name.toLowerCase()}-btn`}
                >
                  {camera.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Subtext */}
      <p className="font-body text-sm mb-4" style={{ color: 'var(--muted)' }}>
        One-time purchase · Instant download · Commercial use included
      </p>
      <p className="font-body text-sm mb-6" style={{ color: 'var(--gold)' }}>
        🔒 30-Day Money-Back Guarantee
      </p>
      
      {/* Feature Tags */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {[
          "20 Cinematic Looks",
          "8 Camera Families",
          ".cube Format",
          "Rec.709 Versions",
          "101 Utility LUTs",
          "Full Documentation"
        ].map((tag, index) => (
          <span 
            key={index}
            className="font-mono text-xs px-4 py-2"
            style={{ backgroundColor: 'var(--dark)', color: 'var(--silver)', fontSize: '10px' }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Payment Trust Badges */}
      <div className="flex flex-wrap justify-center gap-6 text-xs" style={{ color: 'var(--muted)' }}>
        <div className="flex flex-col items-center gap-1">
          <span style={{ fontSize: '20px' }}>🔒</span>
          <span>Secure Checkout</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <span style={{ fontSize: '20px' }}>💳</span>
          <span>Stripe Payment</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <span style={{ fontSize: '20px' }}>✓</span>
          <span>Instant Delivery</span>
        </div>
      </div>
    </div>
  </section>
  );
};

// Sticky Mobile Buy Bar — appears after user scrolls past hero. Mobile only.
const MobileBuyBar = () => {
  const [visible, setVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => {
      // Show after user has scrolled past ~1 viewport height
      setVisible(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleBuy = async () => {
    // Suppress the global bubble-phase AddToCart listener for this click.
    markCheckoutIntent();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: 'full-pack', origin_url: window.location.origin })
      });
      if (!response.ok) throw new Error('Checkout failed');
      const data = await response.json();
      if (data.checkout_url) {
        // Fire pixel with structured payload BEFORE redirect + 250ms flush window.
        trackInitiateCheckout('full-pack');
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 250);
        return;
      }
    } catch (e) {
      console.error(e);
      alert('Unable to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 md:hidden transition-transform duration-300 z-40"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        backgroundColor: 'var(--near-black)',
        borderTop: '1px solid var(--gold-dim)',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.5)',
      }}
      data-testid="mobile-buy-bar"
    >
      <button
        onClick={handleBuy}
        disabled={isLoading}
        className="w-full font-body uppercase text-sm tracking-widest font-semibold py-4 transition-all hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: 'var(--gold)', color: 'var(--black)', letterSpacing: '0.2em' }}
        data-testid="mobile-buy-btn"
      >
        {isLoading ? 'Loading…' : '$49 — Get the Pack'}
      </button>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer 
    className="py-12 px-6 pb-24 md:pb-12"
    style={{ backgroundColor: 'var(--near-black)', borderTop: '1px solid var(--gold-dim)' }}
    data-testid="footer"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
        {/* Left */}
        <div>
          <a 
            href="https://www.skypanpictures.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity inline-block mb-4"
          >
            <img 
              src="/skypan-logo.png" 
              alt="Skypan Pictures" 
              width="200"
              height="60"
              className="h-10 w-auto"
              loading="lazy"
              decoding="async"
            />
          </a>
          <p 
            className="font-heading text-lg block mb-1"
            style={{ color: 'var(--gold)' }}
          >
            SOLARIS
          </p>
        </div>
        
        {/* Center */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <a 
              href="mailto:info@skypanpictures.com"
              className="font-mono text-xs hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="footer-support-link"
            >
              Support
            </a>
            <span style={{ color: 'var(--mid)' }}>·</span>
            <a 
              href="https://www.skypanpictures.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="footer-website-link"
            >
              skypanpictures.com
            </a>
            <span style={{ color: 'var(--mid)' }}>·</span>
            <a 
              href="https://instagram.com/skypanpictures"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="footer-instagram-link"
            >
              @skypanpictures
            </a>
            <span style={{ color: 'var(--mid)' }}>·</span>
            <a 
              href="/terms"
              className="font-mono text-xs hover:text-[#c8a96e] transition-colors"
              style={{ color: 'var(--silver)' }}
              data-testid="footer-terms-link"
            >
              Terms of Use
            </a>
          </div>
        </div>
        
        {/* Right */}
        <div className="text-right">
          <p 
            className="font-mono text-xs"
            style={{ color: 'var(--muted)' }}
          >
            © 2025 Skypan Pictures. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Main App Component
// === MAINTENANCE MODE ===
// Cinematic "back soon" page — same brand as the live site.
// Restore by uncommenting the <Routes> block below and removing this component.
const MaintenancePage = () => (
  <div
    style={{
      minHeight: '100vh',
      backgroundColor: '#070707',
      color: '#f0ece4',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}
    data-testid="maintenance-page"
  >
    {/* Amber vignette matching the site's first-fold atmosphere */}
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse at center, rgba(200,169,110,0.08) 0%, rgba(7,7,7,1) 70%)',
        pointerEvents: 'none',
      }}
    />
    <div style={{ position: 'relative', zIndex: 1 }}>
      <p
        style={{
          fontFamily: "'Courier Prime', 'Courier New', monospace",
          fontSize: '11px',
          letterSpacing: '0.35em',
          color: '#8b857a',
          textTransform: 'uppercase',
          marginBottom: '2rem',
        }}
      >
        Skypan Pictures Presents
      </p>

      <h1
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 400,
          fontSize: 'clamp(3.5rem, 10vw, 6rem)',
          letterSpacing: '0.25em',
          lineHeight: 1,
          margin: 0,
          color: '#f0ece4',
        }}
      >
        SOLAR<span style={{ color: '#c8a96e' }}>IS</span>
      </h1>

      <p
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontStyle: 'italic',
          fontSize: '1rem',
          letterSpacing: '0.05em',
          color: '#8b857a',
          marginTop: '1rem',
          marginBottom: '3rem',
        }}
      >
        A Cinematic LUT Library
      </p>

      {/* Gold center-fade divider */}
      <div
        aria-hidden="true"
        style={{
          height: '1px',
          width: 'min(320px, 70vw)',
          margin: '0 auto 3rem auto',
          background: 'linear-gradient(90deg, rgba(200,169,110,0) 0%, rgba(200,169,110,0.7) 50%, rgba(200,169,110,0) 100%)',
        }}
      />

      <p
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
          fontWeight: 400,
          fontStyle: 'italic',
          color: '#d0c8b8',
          margin: '0 0 1.5rem 0',
          lineHeight: 1.3,
          maxWidth: '640px',
        }}
      >
        More good things on the way.
      </p>

      <p
        style={{
          fontFamily: "'Courier Prime', 'Courier New', monospace",
          fontSize: '10px',
          letterSpacing: '0.3em',
          color: '#5f5a50',
          textTransform: 'uppercase',
          marginTop: '3rem',
        }}
      >
        Back Soon
      </p>
    </div>
  </div>
);

function App() {
  // Site-wide: fire AddToCart when any button expressing buying intent is clicked.
  // Debounced so it doesn't double up with the explicit trackInitiateCheckout path.
  React.useEffect(() => {
    const cleanup = installGlobalBuyClickTracker();
    return cleanup;
  }, []);

  // === MAINTENANCE MODE: every route serves the maintenance page ===
  // To restore the live site: remove this early-return and let the <Routes>
  // block below run as before.
  return (
    <BrowserRouter>
      <Routes>
        <Route path="*" element={<MaintenancePage />} />
      </Routes>
    </BrowserRouter>
  );

  // eslint-disable-next-line no-unreachable
  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div
            style={{
              minHeight: '100vh',
              backgroundColor: '#070707',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#c8a96e',
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.5rem',
              letterSpacing: '0.15em',
            }}
          >
            SOLARIS
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/sony" element={<SonyPackPage />} />
          <Route path="/arri" element={<ARRIPackPage />} />
          <Route path="/red" element={<REDPackPage />} />
          <Route path="/blackmagic" element={<BlackmagicPackPage />} />
          <Route path="/canon" element={<CanonPackPage />} />
          <Route path="/panasonic" element={<PanasonicPackPage />} />
          <Route path="/apple" element={<ApplePackPage />} />
          <Route path="/standard" element={<StandardPackPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/free-lut" element={<FreeLutPage />} />
          <Route path="/redeem" element={<RedeemPage />} />
          <Route path="/flash" element={<FlashPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
          <Route path="/invoice" element={<InvoicePage />} />
          <Route path="/invoice-preview" element={<InvoicePreviewPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

// Testimonials Section
const TestimonialsSection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--black)' }}
    data-testid="testimonials-section"
  >
    <div className="section-divider mb-24"></div>
    
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-16">
        <p 
          className="font-mono text-xs uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
        >
          Filmmakers
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl mb-4" style={{ color: 'var(--white)' }}>
          Join 800+ Filmmakers
        </h2>
        <p className="font-body text-base" style={{ color: 'var(--silver)' }}>
          Trusted by cinematographers, colorists, and content creators worldwide
        </p>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            quote: "SOLARIS transformed my color grading workflow. The LUTs are meticulously crafted and genuinely save time.",
            author: "Sarah Chen",
            role: "Cinematographer"
          },
          {
            quote: "Built on real productions. You can feel the years of color science refinement in every single LUT.",
            author: "Marcus Williams",
            role: "Colorist, Amazon Prime"
          },
          {
            quote: "These aren't generic LUTs. They're a masterclass in color grading wrapped in a purchase.",
            author: "Alex Rivera",
            role: "Director & DP"
          }
        ].map((testimonial, index) => (
          <div 
            key={index}
            className="p-6"
            style={{ backgroundColor: 'var(--near-black)', border: '1px solid var(--mid)' }}
          >
            <p 
              className="font-body text-sm leading-relaxed mb-4 italic"
              style={{ color: 'var(--silver)' }}
            >
              "{testimonial.quote}"
            </p>
            <div>
              <p className="font-mono text-xs uppercase" style={{ color: 'var(--gold)' }}>
                {testimonial.author}
              </p>
              <p className="font-mono text-xs" style={{ color: 'var(--muted)' }}>
                {testimonial.role}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Free LUT CTA Section
const FreeLutCTASection = () => (
  <section 
    className="py-24 px-6"
    style={{ backgroundColor: 'var(--near-black)' }}
    data-testid="free-lut-cta-section"
  >
    <div className="max-w-3xl mx-auto text-center">
      <p 
        className="font-mono text-xs uppercase tracking-widest mb-4"
        style={{ color: 'var(--gold)', letterSpacing: '0.3em' }}
      >
        Free Download
      </p>
      <h2 className="font-heading text-4xl sm:text-5xl mb-4" style={{ color: 'var(--white)' }}>
        Try Before You Buy
      </h2>
      <p 
        className="font-body text-base mb-8"
        style={{ color: 'var(--silver)' }}
      >
        Download our free PEAKY BLINDERS LUT and experience the SOLARIS quality firsthand.
      </p>
      <a
        href="/free-lut"
        className="inline-block font-body uppercase text-sm tracking-widest font-semibold px-8 py-4 transition-all duration-300 hover:opacity-90"
        style={{ backgroundColor: 'var(--gold)', color: 'var(--black)' }}
        data-testid="free-lut-cta-btn"
      >
        Get Free LUT
      </a>
    </div>
  </section>
);

// Main SOLARIS Page Component
const MainPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--black)', overflowX: 'hidden' }}>
      <MainPageSEO />
      <FilmGrain />
      <Navigation />
      {/* First fold — video switcher + Hero CTA share ONE amber-glow backdrop
          so the image doesn't repeat/restart at the section seam. */}
      <div className="relative overflow-hidden" style={{ backgroundColor: 'var(--black)' }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${HERO_BACKGROUNDS.unsplashField})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.22,
            filter: 'brightness(0.7)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center 40%, transparent 5%, rgba(7, 7, 7, 0.92) 100%)',
          }}
        />
        <div className="relative z-10">
          <VideoPreviewSection />
          <Hero />
        </div>
      </div>
      <LooksSection />
      <CameraSection />
      <IncludedSection />
      <StorySection />
      <FAQSection />
      <BuySection />
      <Footer />
      <MobileBuyBar />
    </div>
  );
};

export default App;
